<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      endpoint="/reports/customer-pets/vet-services-by-pet"
      :settings="settings"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <!-- Owner Filter -->
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          value-key="id"
          :placeholder="t('selectOwner')"
          v-model="selectedOwner"
          @change="onSelectedOwnerChanged"
          remote
          :remote-method="searchCustomers"
        >
          <el-option
            v-for="option in customers"
            :key="option.id"
            :label="option.name"
            :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #filter-bottom>
        <div class="flex gap-2">
           <!-- Category Filter -->
          <el-select
            filterable
            clearable
            value-key="id"
            :placeholder="t('selectCategory')"
            v-model="selectedCategory"
            @change="onSelectedCategoryChanged"
          >
            <el-option
              v-for="option in categories"
              :key="option.id"
              :label="option.name"
              :value="option"
            ></el-option>
          </el-select>
          
          <!-- Service Filter -->
          <el-select
            filterable
            clearable
            value-key="id"
            :placeholder="t('selectService')"
            v-model="selectedService"
            @change="onSelectedServiceChanged"
            remote
            :remote-method="searchServices"
          >
            <el-option
              v-for="option in services"
              :key="option.id"
              :label="option.name"
              :value="option"
            ></el-option>
          </el-select>

          <!-- Pet Type Filter -->
          <el-select
            clearable
            :placeholder="t('selectPetType')"
            v-model="selectedPetType"
            @change="onSelectedPetTypeChanged"
          >
            <el-option
              v-for="option in petTypes"
              :key="option"
              :label="option"
              :value="option"
            ></el-option>
          </el-select>

          <!-- Pet Filter -->
          <el-select
            filterable
            clearable
            value-key="id"
            :placeholder="t('selectPet')"
            v-model="selectedPet"
            @change="onSelectedPetChanged"
            remote
            :remote-method="searchPets"
          >
            <el-option
              v-for="option in pets"
              :key="option.id"
              :label="option.name"
              :value="option.id"
            ></el-option>
          </el-select>
        </div>
      </template>

      <template #cards="{ data }">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalPets') }}</div>
              <div class="text-2xl pt-2">{{ data?.totalPets || 0 }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalServices') }}</div>
              <div class="text-2xl pt-2">{{ data?.totalServices || 0 }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalSales') }}</div>
              <div class="text-2xl pt-2">{{ formatMoney(data?.totalSales || 0, $ctx.currency) }}</div>
            </div>
          </el-card>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useHTTP } from '@/plugins/http'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { ReportsTableSettings } from '@/views/reports/v2/table/utls'
import Pet from '@/models/Pet'
import Customer from '@/models/customer/Customer'
import Category from '@/models/inventory/Category'
import Item from '@/models/inventory/Item'
import { useSettingsStore } from '@/stores/settingsStore'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      vetServicesByPet: 'Vet Services by Pet',
      petName: 'Pet Name',
      petType: 'Pet Type',
      numberOfVisits: 'Number of Visits',
      numberOfServices: 'Number of Services',
      ownerName: 'Owner Name',
      totalSales: 'Total Sales',
      totalPets: 'Total Pets',
      totalServices: 'Total Services',
      selectCategory: 'Select Category',
      selectService: 'Select Service',
      selectPetType: 'Select Pet Type',
      selectPet: 'Select Pet',
      selectOwner: 'Select Owner',
    },
    ar: {
      vetServicesByPet: 'خدمات بيطرية حسب الأليف',
      petName: 'اسم الأليف',
      petType: 'نوع الأليف',
      numberOfVisits: 'عدد الزيارات',
      numberOfServices: 'عدد الخدمات',
      ownerName: 'اسم المالك',
      totalSales: 'مبيعات الخدمات البيطرية',
      totalPets: 'عدد الحيوانات الاليفة',
      totalServices: 'الخدمات البيطرية المقدمة',
      selectCategory: 'اختر الفئة',
      selectService: 'اختر الخدمة',
      selectPetType: 'اختر نوع الأليف',
      selectPet: 'اختر الأليف',
      selectOwner: 'اختر المالك',
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])
const http = useHTTP()

const settings = ref<Partial<ReportsTableSettings>>({
  hideTable: false,
  customRowsKey: 'pets',
  title: t('vetServicesByPet'),
  hideCards: false,
  hideExport: false,
  hideFilter: false,
  appendQueryParams: {},
  theme: 'ag-theme-balham'
})

const categories = ref<Category[]>([])
const selectedCategory = ref<any>()

const services = ref<Item[]>([])
const selectedService = ref<any>()

const petTypes = ref<string[]>([])
const selectedPetType = ref<string>()

const pets = ref<Pet[]>([])
const selectedPet = ref<any>()

const customers = ref<Customer[]>([])
const selectedOwner = ref<any>()

onBeforeMount(async () => {
  // Load pet types from settings
  const settingsStore = useSettingsStore()
  const petTypesValue = settingsStore.getValue('pet_types')
  if (petTypesValue) {
    try {
      petTypes.value = typeof petTypesValue === 'string'
        ? JSON.parse(petTypesValue)
        : petTypesValue
    } catch (e) {
      console.error('Error parsing pet_types:', e)
      petTypes.value = []
    }
  }

  columnDefs.value = [
    {
      headerName: t('petName'),
      field: 'petName',
      checkboxSelection: true,
      headerCheckboxSelection: true
    },
    {
      headerName: t('petType'),
      field: 'petType'
    },
    {
      headerName: t('numberOfVisits'),
      field: 'numberOfVisits',
      cellStyle: { direction: 'ltr' }
    },
    {
      headerName: t('numberOfServices'),
      field: 'numberOfServices',
      cellStyle: { direction: 'ltr' }
    },
    {
      headerName: t('ownerName'),
      field: 'ownerName'
    },
    {
      headerName: t('totalSales'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.totalSales, data.currency)
      },
      cellStyle: { direction: 'ltr' }
    }
  ]

  // Load categories
  try {
    const categoriesRes = await http.get<Category[]>(Category.ENDPOINT)
    categories.value = categoriesRes.map((category) => Category.from(category))
  } catch (error) {
    console.error('Failed to load categories:', error)
  }
})

function onDataLoaded(_data: any) {
  // Handle data loaded event if needed
}

async function onSelectedCategoryChanged(_event: any) {
  if (!rtable.value || !settings.value.appendQueryParams) return

  settings.value.appendQueryParams.category_id = selectedCategory.value?.id || null
  rtable.value.navigate(rtable.value.payload)
}

async function onSelectedServiceChanged(_event: any) {
  if (!rtable.value || !settings.value.appendQueryParams) return

  settings.value.appendQueryParams.service_id = selectedService.value?.id || null
  rtable.value.navigate(rtable.value.payload)
}

async function onSelectedPetTypeChanged(_event: any) {
  if (!rtable.value || !settings.value.appendQueryParams) return

  settings.value.appendQueryParams.pet_type = selectedPetType.value || null
  rtable.value.navigate(rtable.value.payload)
}

async function onSelectedPetChanged(_event: any) {
  if (!rtable.value || !settings.value.appendQueryParams) return

  settings.value.appendQueryParams.pet_id = selectedPet.value || null
  rtable.value.navigate(rtable.value.payload)
}

async function onSelectedOwnerChanged(_event: any) {
  if (!rtable.value || !settings.value.appendQueryParams) return

  settings.value.appendQueryParams.owner_id = selectedOwner.value?.id || null
  rtable.value.navigate(rtable.value.payload)
}

async function searchServices(query: string) {
  if (!query) {
    query = ''
  }
  try {
    let url = `/inventory/items/vet-service-items-lookup?q=${query}`
    if (selectedCategory.value?.id) {
      url += `&category=${selectedCategory.value.id}`
    }
    const servicesRes = await http.get<Item[]>(url)
    services.value = servicesRes.map((service) => Item.from(service))
  } catch (error) {
    services.value = []
  }
}

async function searchPets(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Pet.ENDPOINT}?q=${query}`
    const petsRes = await http.get<any>(url)
    pets.value = petsRes.map((pet: any) => Pet.from(pet))
    console.log(pets.value)
  } catch (error) {
    pets.value = []
  }
}

async function searchCustomers(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Customer.ENDPOINT}?q=${query}`
    const customersRes = await http.get<Customer[]>(url)
    customers.value = customersRes.map((customer) => Customer.from(customer))
  } catch (error) {
    customers.value = []
  }
}
</script>
